'use client'

import { animate, motion, useInView, useMotionValue, useSpring } from 'framer-motion'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getCdnPath } from '~/lib/utils'
import { FeatureCard } from './feature-card'

interface FlickeringGridProps {
  squareSize?: number
  gridGap?: number
  flickerChance?: number
  color?: string
  width?: number
  height?: number
  className?: string
  maxOpacity?: number
}

function FlickeringGrid({
  squareSize = 4,
  gridGap = 6,
  flickerChance = 0.3,
  color = 'rgb(0, 0, 0)',
  width,
  height,
  className,
  maxOpacity = 0.3,
}: FlickeringGridProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [isInView, setIsInView] = useState(false)
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 })

  // Animated flickerChance using Framer Motion
  const animatedFlickerChance = useMotionValue(flickerChance)
  const smoothFlickerChance = useSpring(animatedFlickerChance, {
    stiffness: 100,
    damping: 30,
  })

  // Start animation after 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      animate(animatedFlickerChance, 0, {
        duration: 2,
        ease: 'easeOut',
      })
    }, 500)

    return () => clearTimeout(timer)
  }, [animatedFlickerChance])

  const memoizedColor = useMemo(() => {
    const toRGBA = (color: string) => {
      if (typeof window === 'undefined') {
        return `rgba(0, 0, 0,`
      }
      const canvas = document.createElement('canvas')
      canvas.width = canvas.height = 1
      const ctx = canvas.getContext('2d')
      if (!ctx)
        return 'rgba(255, 0, 0,'
      ctx.fillStyle = color
      ctx.fillRect(0, 0, 1, 1)
      const [r, g, b] = Array.from(ctx.getImageData(0, 0, 1, 1).data)
      return `rgba(${r}, ${g}, ${b},`
    }
    return toRGBA(color)
  }, [color])

  const setupCanvas = useCallback(
    (canvas: HTMLCanvasElement, width: number, height: number) => {
      const dpr = window.devicePixelRatio || 1
      canvas.width = width * dpr
      canvas.height = height * dpr
      canvas.style.width = `${width}px`
      canvas.style.height = `${height}px`
      const cols = Math.floor(width / (squareSize + gridGap))
      const rows = Math.floor(height / (squareSize + gridGap))

      const squares = new Float32Array(cols * rows)
      for (let i = 0; i < squares.length; i++) {
        squares[i] = Math.random() * maxOpacity
      }

      return { cols, rows, squares, dpr }
    },
    [squareSize, gridGap, maxOpacity],
  )

  const updateSquares = useCallback(
    (squares: Float32Array, deltaTime: number, currentFlickerChance: number) => {
      for (let i = 0; i < squares.length; i++) {
        if (Math.random() < currentFlickerChance * deltaTime) {
          squares[i] = Math.random() * maxOpacity
        }
      }
    },
    [maxOpacity],
  )

  const drawGrid = useCallback(
    (
      ctx: CanvasRenderingContext2D,
      width: number,
      height: number,
      cols: number,
      rows: number,
      squares: Float32Array,
      dpr: number,
    ) => {
      ctx.clearRect(0, 0, width, height)
      ctx.fillStyle = 'transparent'
      ctx.fillRect(0, 0, width, height)

      for (let i = 0; i < cols; i++) {
        for (let j = 0; j < rows; j++) {
          const opacity = squares[i * rows + j]
          ctx.fillStyle = `${memoizedColor}${opacity})`
          ctx.fillRect(
            i * (squareSize + gridGap) * dpr,
            j * (squareSize + gridGap) * dpr,
            squareSize * dpr,
            squareSize * dpr,
          )
        }
      }
    },
    [memoizedColor, squareSize, gridGap],
  )

  useEffect(() => {
    const canvas = canvasRef.current
    const container = containerRef.current
    if (!canvas || !container)
      return

    const ctx = canvas.getContext('2d')
    if (!ctx)
      return

    let animationFrameId: number
    let gridParams: ReturnType<typeof setupCanvas>

    const updateCanvasSize = () => {
      const newWidth = width || container.clientWidth
      const newHeight = height || container.clientHeight
      setCanvasSize({ width: newWidth, height: newHeight })
      gridParams = setupCanvas(canvas, newWidth, newHeight)
    }

    updateCanvasSize()

    let lastTime = 0
    const animate = (time: number) => {
      if (!isInView)
        return

      const deltaTime = (time - lastTime) / 1000
      lastTime = time

      updateSquares(gridParams.squares, deltaTime, smoothFlickerChance.get())
      drawGrid(ctx, canvas.width, canvas.height, gridParams.cols, gridParams.rows, gridParams.squares, gridParams.dpr)
      animationFrameId = requestAnimationFrame(animate)
    }

    const resizeObserver = new ResizeObserver(() => {
      updateCanvasSize()
    })

    resizeObserver.observe(container)

    const intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting)
      },
      { threshold: 0 },
    )

    intersectionObserver.observe(canvas)

    if (isInView) {
      animationFrameId = requestAnimationFrame(animate)
    }

    return () => {
      cancelAnimationFrame(animationFrameId)
      resizeObserver.disconnect()
      intersectionObserver.disconnect()
    }
  }, [setupCanvas, updateSquares, drawGrid, width, height, isInView])

  return (
    <div
      ref={containerRef}
      className={`
        h-full w-full
        ${className}
      `}
    >
      <canvas
        ref={canvasRef}
        className="pointer-events-none"
        style={{
          width: canvasSize.width,
          height: canvasSize.height,
        }}
      />
    </div>
  )
}

interface AICoachCardProps {
  className?: string
}

export function AICoachCard(props: AICoachCardProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)
  const [flickeringWidth, setFlickeringWidth] = useState(300)
  const isInView = useInView(imageRef, { once: true, amount: 0.3 })

  useEffect(() => {
    const updateGridWidth = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.clientWidth
        setFlickeringWidth(Math.round(containerWidth * 1.1))
      }
    }

    updateGridWidth()

    const resizeObserver = new ResizeObserver(updateGridWidth)
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current)
    }

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  return (
    <FeatureCard
      ref={containerRef}
      className={props.className}
      background={(
        <>
          <div className="relative z-10 mt-10 flex justify-center">
            <motion.img
              ref={imageRef}
              src={getCdnPath('/GnD8aDOXUAAsnzH.jpeg')}
              alt="Иконка нейро коуча для AI-анализа интервью"
              className="size-32 rounded-full shadow-xl"
              initial={{ scale: 0.8, opacity: 0, rotate: -10 }}
              animate={isInView ? { scale: 1, opacity: 1, rotate: 0 } : { scale: 0.8, opacity: 0, rotate: -10 }}
              transition={{
                duration: 0.8,
                ease: [0.6, -0.05, 0.01, 0.99],
                delay: 0.2,
              }}
              whileHover={{
                scale: 1.1,
                rotate: 5,
                transition: { duration: 0.3, ease: 'easeOut' },
              }}
            />
          </div>

          <div className={`
            absolute inset-0 z-1 size-full bg-gradient-to-t from-white from-5% to-transparent
            dark:from-black/80 dark:via-black/20
          `}
          />

          <FlickeringGrid
            className="absolute top-0 z-0 size-full"
            squareSize={4}
            gridGap={6}
            color="#6D28D9"
            maxOpacity={0.5}
            flickerChance={5}
            width={flickeringWidth}
          />
        </>
      )}
      title="AI-интервью"
      titleChromaTextAnimate
      description="Интеллектуальный анализ ваших ответов с персонализированными советами и практическими рекомендациями для успешного прохождения интервью."
    />
  )
}
